import dynamic from 'next/dynamic';
import PropTypes from 'prop-types';
import { memo, useState } from 'react';

import { Dropdown } from 'shopper/components/Dropdown';

import ErrorBoundary from 'components/ErrorBoundary';
import GenericError from 'components/GenericError';
import SitemapGroupList from 'components/Topbar/SitemapGroupList';

import useCategoriesWithLandings from 'hooks/useCategoriesWithHighlightedLandings';

import placeholder from 'lib/placeholder';

import CategoriesGroups from './CategoriesGroups';

const ActiveCategoryGroupList = dynamic(
  () => import('./ActiveCategoryGroupList'),
  { ssr: false }
);
const Overlay = dynamic(() => import('shopper/components/Overlay'), {
  ssr: false,
});

const CategoriesDropdown = ({ isOpen, openerRef, onClose }) => {
  const [activeCategory, setActiveCategory] = useState(null);
  const { categoriesWithHighlightedLandings } = useCategoriesWithLandings();

  const onCloseHandler = (e) => {
    setActiveCategory(null);
    onClose(e);
  };

  return (
    <>
      {isOpen && (
        <>
          <Overlay className="w-full" />
          <Dropdown
            className="z-50 rounded-3 bg-neutral-high-100 shadow-[0px_0px_10px_0px_rgba(87,87,87,0.2)]"
            data-test-selector="categories-dropdown"
            id="categories-dropdown"
            open={isOpen}
            openerRef={openerRef}
            renderToHTML
            onClickOutside={onClose}
          >
            <ErrorBoundary
              fallback={
                <div className="w-72">
                  <GenericError
                    message={placeholder('errors.couldNotLoadCategories')}
                  />
                </div>
              }
            >
              <ul className="flex" onMouseLeave={onCloseHandler}>
                <CategoriesGroups
                  activeCategory={activeCategory}
                  categories={categoriesWithHighlightedLandings}
                  onSelect={setActiveCategory}
                />
                {activeCategory && (
                  <ActiveCategoryGroupList
                    activeCategory={activeCategory}
                    categories={categoriesWithHighlightedLandings}
                    onClose={onCloseHandler}
                  />
                )}
              </ul>
            </ErrorBoundary>
          </Dropdown>
        </>
      )}
      {/* This is used for SEO purposes... */}
      <SitemapGroupList categories={categoriesWithHighlightedLandings} />
    </>
  );
};

CategoriesDropdown.propTypes = {
  activeCategory: PropTypes.string,
  isOpen: PropTypes.bool.isRequired,
  openerRef: PropTypes.shape().isRequired,
  setActiveCategory: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default memo(CategoriesDropdown);
